/* eslint-disable react/button-has-type */
/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */

"use client"

import clsx from "clsx"
import useEmblaCarousel from "embla-carousel-react"
import Link from "next/link"
import React, { useState, useEffect, useRef, useCallback } from "react"

import DevCard1 from "../../../../public/developer-resources/dev-card-01.jpg"
import DevCard2 from "../../../../public/developer-resources/dev-card-02.jpg"
import DevCard3 from "../../../../public/developer-resources/dev-card-03.jpg"
import ArrowCarousel from "../../../../public/icons/arrow-carousel.svg"
import LinkOut from "../../../../public/icons/link-out.svg"
import PlusIcon from "../../../../public/icons/plus-icon.svg"
import BackgroundImage from "../BackgroundImage"

const slideContent = [
  {
    title: "Multi-chain support",
    content: [
      "RippleX developers have introduced tools to create, launch, and test Federated Sidechains for the XRP Ledger. Developers can run a Federated Sidechain to explore a number of use cases, like private/permissioned ledgers and smart contract implementation—without compromising the XRPL’s existing feature set or efficiency.",
      "Developers can now build Federated Sidechains and connect them directly to the XRPL Devnet, Testnet or Mainnet.",
    ],
    ctaLink:
      "https://opensource.ripple.com/docs/xls-38d-cross-chain-bridge/cross-chain-bridges",
    bgImage: DevCard1.src,
  },
  {
    title: "Non-fungible token (NFT) support (XLS-20d)",
    content: [
      `Together with the community, the XLS-20d proposal for native NFT
      capabilities on the XRP Ledger has been released on a public development network, NFT-Devnet. NFT support has also been added to client libraries, xrpl.js & xrpl-py, as well as the Bithomp & xrpl.org explorers.`,
      "Now developers can begin experimenting with native NFTs on the ledger with the full support of the XRPL community.",
    ],
    ctaLink: "https://xrpl.org/docs/use-cases/tokenization/nft-mkt-overview",
    bgImage: DevCard2.src,
  },
  {
    title: "Project Clio",
    content: [
      "Dedicated to the continued stability of the XRPL, RippleX developers are building Project Clio—an XRPL API server heavily optimized for scalability and availability.",
      "Clio stores ledger and transaction data in significantly less space than rippled (the core server that powers the XRPL) and also removes the burden of handling API requests from rippled. It allows more users to reliably interact with the ledger and helps rippled process transactions more efficiently.",
    ],
    bgImage: DevCard3.src,
  },
]

interface LearnMoreButtonProps {
  learnMore: boolean
  setLearnMore: React.Dispatch<React.SetStateAction<boolean>>
}
const LearnMoreButton: React.FC<LearnMoreButtonProps> = ({
  learnMore,
  setLearnMore,
}) => (
  <button
    onClick={() => setLearnMore(!learnMore)}
    className="flex items-center space-x-2"
  >
    <div className="flex items-center justify-center md:w-6 md:h-6 lg:w-10 lg:h-10 group">
      <div
        className={clsx(
          "p-1 border-2 lg:p-2 rounded-full group-hover:bg-gray-2.5",
          {
            "transition-transform ease-in duration-100 rotate-45": learnMore,
          },
          {
            "transition-transform ease-in duration-100 rotate-0": !learnMore,
          },
        )}
      >
        <PlusIcon className="group-hover:stroke-black-100" />
      </div>
    </div>
    <div className={clsx("text-gray-2.5", { hidden: learnMore })}>
      Learn More
    </div>
    <div
      className={clsx("text-gray-2.5", {
        hidden: !learnMore,
      })}
    >
      Read Less
    </div>
  </button>
)

const LatestContributionsSection: React.FC = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    skipSnaps: false,
    dragFree: false,
    duration: 20,
    slidesToScroll: 1,
  })
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const divRef = useRef<HTMLInputElement>(null)

  // Set to 32 since this is the minimum left margin spacing
  const [leftMargin, setLeftMargin] = React.useState(32)

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback((api: any) => {
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on("reInit", onSelect)
    emblaApi.on("select", onSelect)
  }, [emblaApi, onSelect])

  useEffect(() => {
    function handleResize() {
      // This function is used to calculate the offset needed from the Carousel section
      // We set an id to the wrapper which is using the PageGrid grid
      // We then set the margin-left to the inner carousel wrapper
      // This auto updates on load and resize
      const offset = divRef.current?.offsetLeft
      // eslint-disable-next-line no-void, no-unused-expressions
      offset !== undefined && void setLeftMargin(offset)
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  })

  return (
    <div className="py-9 md:py-10 lg:py-16 xl:py-28 bg-gray-2.5 text-gray-85">
      <div
        className="mx-4 lg:max-w-layout-lg lg:mx-auto xl:max-w-layout-xl"
        ref={divRef}
      >
        <h4 className="p-4 mb-12 text-gray-90 heading1 md:mb-20 md:headline5 lg:mb-16 xl:headline3">
          Explore RippleX&rsquo;s latest contributions to the XRP Ledger
        </h4>
      </div>
      <div className="pr-4 md:pr-0" style={{ marginLeft: leftMargin }}>
        {/* Embla viewport */}
        <div className="embla" ref={emblaRef}>
          <div className="embla__container">
            {slideContent.map((slide) => {
              // eslint-disable-next-line react-hooks/rules-of-hooks
              const [learnMore, setLearnMore] = useState(false)

              return (
                <div
                  key={slide.title}
                  className="embla__slide flex-[0_0_100%] md:flex-[0_0_60%] lg:flex-[0_0_60%] xl:flex-[0_0_60%] min-w-0 md:pr-8"
                >
                  <div className="relative rounded-lg md:rounded lg:rounded-md xl:rounded-lg">
                    <BackgroundImage
                      className="p-8 bg-no-repeat bg-cover xl:h-[520px] lg:h-[433px] md:h-[298px] h-[424px] md:rounded lg:rounded-md xl:rounded-lg"
                      image={slide.bgImage}
                    >
                      <div
                        className={clsx(
                          "absolute inset-0 z-0 overflow-hidden rounded-lg bg-gradient-to-t from-black-100 md:rounded lg:rounded-md xl:rounded-lg",
                          learnMore ? "!bg-gray-85" : "",
                        )}
                      />

                      <div className="absolute inset-0 z-10 flex flex-col justify-end p-8">
                        <div className="mb-1 overflow-x-auto md:mb-3 lg:mb-5 xl:mb-6 ">
                          <div
                            className={clsx({
                              hidden: learnMore,
                            })}
                          >
                            {!slide.ctaLink && (
                              <p className="w-max px-3 py-2 button2 text-gray-90 bg-green-20 rounded-[40px] mb-6">
                                Coming Soon
                              </p>
                            )}
                            <h5 className="text-gray-2.5 heading2 md:heading1 lg:headline5">
                              {slide.title}
                            </h5>
                          </div>
                          <div
                            className={clsx(
                              "text-gray-2.5 flex flex-col space-y-6 md:pr-8",
                              {
                                hidden: !learnMore,
                              },
                            )}
                          >
                            {!slide.ctaLink && (
                              <p className="w-max px-3 py-2 button2 text-gray-90 bg-green-20 rounded-[40px]">
                                Coming Soon
                              </p>
                            )}
                            <h5 className="heading2 md:heading1 lg:headline5">
                              {slide.title}
                            </h5>
                            <div>
                              {slide.content.map((para, dex) => (
                                <p
                                  key={para}
                                  className={clsx(
                                    "body4 md:body3 lg:body2",
                                    dex === 0 ? "mb-4" : "",
                                  )}
                                >
                                  {para}
                                </p>
                              ))}
                            </div>
                            {slide.ctaLink && (
                              <Link
                                href={slide.ctaLink}
                                className="flex items-center align-left body2 text-blue-50"
                              >
                                Try it
                                <LinkOut
                                  title="Link Out Icon"
                                  className="ml-2 stroke-blue-50"
                                />
                              </Link>
                            )}
                          </div>
                        </div>
                        <LearnMoreButton
                          learnMore={learnMore}
                          setLearnMore={setLearnMore}
                        />
                      </div>
                    </BackgroundImage>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Navigation controls */}
        <div className="flex pl-4 mt-10 md:mt-8 relative z-10">
          <button
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            className={clsx(
              "stroke-black disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-50 cursor-pointer relative z-10",
            )}
            type="button"
            aria-label="Previous slide"
          >
            <ArrowCarousel className="mr-10 transform -rotate-180 h-14 w-14" />
          </button>
          <button
            onClick={scrollNext}
            disabled={!canScrollNext}
            className={clsx(
              "stroke-black disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-50 cursor-pointer relative z-10",
            )}
            type="button"
            aria-label="Next slide"
          >
            <ArrowCarousel className="h-14 w-14" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default LatestContributionsSection
